/* global Office, Word */

/**
 * Track Changes tool executors.
 *
 * Redlines are Word's NATIVE tracked revisions (ADR-060): we toggle
 * `document.changeTrackingMode` and let the existing write tools produce
 * revisions — there is no bespoke "apply tracked change" path. Edits run
 * in the signed-in user's session, so revisions are attributed to the
 * human user by construction.
 *
 * `getTrackedChanges()` / accept / reject require WordApi 1.6; mode
 * toggling requires WordApi 1.4. Both are guarded with friendly errors
 * for older Word builds.
 */

import { getEffectiveSelectionRange } from "../selection-tracker";
import { getString } from "./utils";

const MODE_MAP: Record<string, Word.ChangeTrackingMode> = {
  trackAll: "TrackAll" as Word.ChangeTrackingMode,
  off: "Off" as Word.ChangeTrackingMode,
};

function requireApiSet(version: string, feature: string): void {
  if (
    typeof Office !== "undefined" &&
    Office.context?.requirements &&
    !Office.context.requirements.isSetSupported("WordApi", version)
  ) {
    throw new Error(
      `${feature} requires WordApi ${version}+, which this Word build does not support. ` +
        "Ask the user to update Word (Microsoft 365 current channel) to use track changes."
    );
  }
}

export function setTrackChangesCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): { mode: string; ok: true } {
  requireApiSet("1.4", "Toggling track changes");
  const mode = getString(args, "mode");
  const resolved = MODE_MAP[mode];
  if (!resolved) {
    throw new Error(`Invalid mode "${mode}". Expected one of: trackAll, off.`);
  }
  ctx.document.changeTrackingMode = resolved;
  return { ok: true, mode };
}

interface TrackedChangeFilters {
  authorFilter?: string;
  indexFilter?: Set<number>;
  textFilter?: string;
  typeFilter?: string;
}

interface MappedTrackedChange {
  author: string;
  date: string;
  index: number;
  text: string;
  type: string;
}

function parseTrackedChangeFilters(
  args: Record<string, unknown>
): TrackedChangeFilters {
  const authorFilter = getString(args, "author", false)?.toLowerCase();
  const typeFilter = getString(args, "type", false)?.toLowerCase();
  const textFilter = getString(args, "containsText", false)?.toLowerCase();
  if (typeFilter && !["added", "deleted", "formatted"].includes(typeFilter)) {
    throw new Error(
      `Invalid type "${typeFilter}". Expected one of: added, deleted, formatted.`
    );
  }
  let indexFilter: Set<number> | undefined;
  const rawIndexes = args.indexes;
  if (rawIndexes != null) {
    if (
      !Array.isArray(rawIndexes) ||
      rawIndexes.some((i) => !Number.isInteger(i) || (i as number) < 0)
    ) {
      throw new Error(
        "Argument indexes must be an array of non-negative integers."
      );
    }
    indexFilter = new Set(rawIndexes as number[]);
  }
  return { authorFilter, typeFilter, textFilter, indexFilter };
}

function hasAnyFilter(filters: TrackedChangeFilters): boolean {
  return Boolean(
    filters.authorFilter ||
      filters.typeFilter ||
      filters.textFilter ||
      filters.indexFilter
  );
}

function matchesFilters(
  change: MappedTrackedChange,
  filters: TrackedChangeFilters
): boolean {
  const { authorFilter, typeFilter, textFilter, indexFilter } = filters;
  return (
    (!authorFilter || change.author.toLowerCase().includes(authorFilter)) &&
    (!typeFilter || change.type.toLowerCase() === typeFilter) &&
    (!textFilter || change.text.toLowerCase().includes(textFilter)) &&
    (!indexFilter || indexFilter.has(change.index))
  );
}

const TRACKED_CHANGE_LOAD_PROPS =
  "items/author,items/date,items/text,items/type";

function mapTrackedChanges(items: Word.TrackedChange[]): MappedTrackedChange[] {
  return items.map((c, index) => ({
    author: c.author ?? "",
    date: c.date ? new Date(c.date as unknown as string).toISOString() : "",
    index,
    text: c.text ?? "",
    type: String(c.type ?? ""),
  }));
}

export async function getTrackedChangesCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{
  changes: MappedTrackedChange[];
  mode: string;
  total: number;
  totalUnfiltered: number;
  truncated: boolean;
}> {
  requireApiSet("1.6", "Reading tracked changes");
  const filters = parseTrackedChangeFilters(args);

  ctx.document.load("changeTrackingMode");
  const trackedChanges = ctx.document.body.getTrackedChanges();
  trackedChanges.load(TRACKED_CHANGE_LOAD_PROPS);
  await ctx.sync();

  // Office.js has no server-side revision filter — load everything and
  // filter locally (mirrors Word's "Show Markup → Specific People").
  // `index` is the position in the UNFILTERED collection so it stays
  // valid as an accept/reject `indexes` target.
  const all = mapTrackedChanges(trackedChanges.items);
  const filtered = all.filter((c) => matchesFilters(c, filters));

  const HARD_CAP = 200;
  const truncated = filtered.length > HARD_CAP;
  return {
    changes: truncated ? filtered.slice(0, HARD_CAP) : filtered,
    mode: String(ctx.document.changeTrackingMode ?? ""),
    total: filtered.length,
    totalUnfiltered: all.length,
    truncated,
  };
}

async function resolveTrackedChanges(
  ctx: Word.RequestContext,
  args: Record<string, unknown>
): Promise<{ scope: string; trackedChanges: Word.TrackedChangeCollection }> {
  const scope = getString(args, "scope");
  if (scope === "all") {
    return { scope, trackedChanges: ctx.document.body.getTrackedChanges() };
  }
  if (scope === "selection") {
    const effective = await getEffectiveSelectionRange(ctx);
    if (!effective) {
      throw new Error(
        "No selection is active. Ask the user to highlight the revisions to act on, or use scope='all'."
      );
    }
    return { scope, trackedChanges: effective.range.getTrackedChanges() };
  }
  throw new Error(
    `Invalid scope "${scope}". Expected one of: all, selection, filtered.`
  );
}

/**
 * Resolve the per-revision proxies matching the scope='filtered' args.
 * Loads the full body collection, maps + filters locally (same logic as
 * `get_tracked_changes`, so `indexes` returned there line up here), and
 * returns the matching `Word.TrackedChange` items plus their summaries.
 */
async function resolveFilteredItems(
  ctx: Word.RequestContext,
  args: Record<string, unknown>
): Promise<{ items: Word.TrackedChange[]; matched: MappedTrackedChange[] }> {
  const filters = parseTrackedChangeFilters(args);
  if (!hasAnyFilter(filters)) {
    throw new Error(
      "scope='filtered' requires at least one of: author, type, containsText, indexes. " +
        "Use scope='all' to act on every revision."
    );
  }
  const trackedChanges = ctx.document.body.getTrackedChanges();
  trackedChanges.load(TRACKED_CHANGE_LOAD_PROPS);
  await ctx.sync();

  const mapped = mapTrackedChanges(trackedChanges.items);
  const matched = mapped.filter((c) => matchesFilters(c, filters));
  const items = matched.map(
    (c) => trackedChanges.items[c.index] as Word.TrackedChange
  );
  return { items, matched };
}

type TrackedChangeAction = "accept" | "reject";

async function actOnTrackedChanges(
  ctx: Word.RequestContext,
  args: Record<string, unknown>,
  action: TrackedChangeAction
): Promise<{ count: number; matched?: MappedTrackedChange[]; scope: string }> {
  if (getString(args, "scope", false) === "filtered") {
    const { items, matched } = await resolveFilteredItems(ctx, args);
    // Per-revision accept/reject. Queue all, then one sync — Word
    // resolves each revision independently.
    for (const item of items) {
      if (action === "accept") {
        item.accept();
      } else {
        item.reject();
      }
    }
    if (items.length > 0) {
      await ctx.sync();
    }
    return { scope: "filtered", count: items.length, matched };
  }

  const { scope, trackedChanges } = await resolveTrackedChanges(ctx, args);
  trackedChanges.load("items");
  await ctx.sync();
  const count = trackedChanges.items.length;
  if (count > 0) {
    if (action === "accept") {
      trackedChanges.acceptAll();
    } else {
      trackedChanges.rejectAll();
    }
    await ctx.sync();
  }
  return { scope, count };
}

export async function acceptTrackedChangesCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{
  accepted: number;
  matched?: MappedTrackedChange[];
  ok: true;
  scope: string;
}> {
  requireApiSet("1.6", "Accepting tracked changes");
  const { scope, count, matched } = await actOnTrackedChanges(
    ctx,
    args,
    "accept"
  );
  return { ok: true, scope, accepted: count, matched };
}

export async function rejectTrackedChangesCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{
  matched?: MappedTrackedChange[];
  ok: true;
  rejected: number;
  scope: string;
}> {
  requireApiSet("1.6", "Rejecting tracked changes");
  const { scope, count, matched } = await actOnTrackedChanges(
    ctx,
    args,
    "reject"
  );
  return { ok: true, scope, rejected: count, matched };
}
